// 수업 리뷰 > AI 진단 개요
import { QIcon_check_filled, QIcon_check_outline } from '@repo/icons/index'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { CAFOverviewResponse, CAF_ANALYSIS_STATUS } from '../../../../../model/caf.interface'
import apis from '../../../../../modules/apis'
import { useCafOverviewCheckedV5 } from '../../../../../modules/apis/webview/caf'
import { axiosPost } from '../../../../../modules/axiosHelper'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import { n_review_lessons } from '../../../../../modules/i18n/strings/auto/wording'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { COLORS, STUDY_REVIEW } from '../../../../../modules/vars'
import CafBanner from '../../../../banner/caf/CafBanner'
import { MobileDivider } from '../../../../common/Divider'
import TIPSection from '../../common/TIPSection'
import HighlightListSection from '../../overview/HighlightListSection'
import OverviewIntroSection from '../../overview/OverviewIntroSection'
import PinnedHighlights from '../../overview/PinnedHighlights'

interface Props {
  overallData: any
  isLoading: boolean
  status: CAF_ANALYSIS_STATUS
  todoChecked: boolean
  setTodoChecked: (checked: boolean) => void
}
const CAFOverviewSummary = ({ overallData, isLoading, status, todoChecked, setTodoChecked }: Props) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
  const { t } = useTranslation()
  const { mutate: checkOverview } = useCafOverviewCheckedV5()
  const isMobile = useIsMobile()

  const checkCAFTodo = async () => {
    const payload = {
      action_type: STUDY_REVIEW.CAF,
    }
    const response = await axiosPost(apis.lessons.review.study(lessonId), payload)
    if (response?.success) {
      setTodoChecked(true)
      toast(t(n_caf.overview.check_caf_analysis.toast))
    } else {
      toast(t('Please try again.'))
    }
  }
  useEffect(() => {
    if (status === CAF_ANALYSIS_STATUS.READY) {
      checkOverview(+lessonId)
    }
  }, [lessonId, isLoading, status, checkOverview])

  return (
    <>
      <div className=" flex h-[100%] w-full flex-col items-center justify-start px-[20px] pt-[20px] sm:px-0 sm:pt-[32px] xl:w-[688px]">
        <CafBanner
          pageType="review"
          lessonBannerShow={overallData?.show_popup}
          lessonBannerType={overallData?.popup_type}
        />
        <TopSection data={overallData} isLoading={isLoading} />
        <RecommendedHighlightsSection list={overallData?.components || []} />
        <PinnedHighlightsSection list={overallData?.components || []} />
      </div>
      {isMobile && <MobileDivider className="mt-[16px]" />}
      <div className=" flex h-[100%] w-full flex-col items-center justify-start px-[20px] sm:px-0 xl:w-[688px]">
        {isMobile && <TIPSection />}
        <AITodoBox isChecked={todoChecked} onClickCheck={checkCAFTodo} />
        <div className="h-[60px]"></div>
      </div>
    </>
  )
}

interface TopSectionProps {
  data: CAFOverviewResponse
  isLoading: boolean
}
const TopSection = ({ data, isLoading }: TopSectionProps) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
  return <OverviewIntroSection data={data} isLoading={isLoading} lessonId={lessonId} />
}
interface HighlightsProps {
  list: CAFOverviewResponse['components']
}
const RecommendedHighlightsSection = ({ list }: HighlightsProps) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
  const overallLevel = list?.find((compItem) => compItem.component_tag === 0)?.level_rough
  return <HighlightListSection lessonId={lessonId} level={overallLevel} />
}
const PinnedHighlightsSection = ({ list }: HighlightsProps) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
  const overallLevel = list?.find((compItem) => compItem.component_tag === 0)?.level_rough
  return <PinnedHighlights lessonId={+lessonId} level={overallLevel} />
}
interface TodoProps {
  isChecked: boolean
  onClickCheck: () => void
}
const AITodoBox = ({ isChecked, onClickCheck }: TodoProps) => {
  const { t } = useTranslation()
  return (
    <div
      className="flex cursor-pointer items-center justify-center gap-[8px] whitespace-nowrap rounded-[7px] border-[1.5px] border-gray-300 px-[20px] py-[16px] lg:hidden"
      onClick={onClickCheck}
    >
      {isChecked ? (
        <QIcon_check_filled className="h-[20px] w-[20px]" color={COLORS.GREEN500} />
      ) : (
        <QIcon_check_outline className="h-[20px] w-[20px]" color={COLORS.GRAY500} />
      )}
      <span className="Body1-16Bd text-black">{t(n_review_lessons.review_completed_caf)}</span>
    </div>
  )
}

export default CAFOverviewSummary
